/*
 * *****************************************************************************
 *     Copyright (C) 2024 Motorola Solutions, Inc.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ServiceHealth } from '../model/health';
import { BehaviorSubject, defer, interval, Observable, of, ReplaySubject, retry, Subscription, throwError, timeout, timer } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ServiceConfiguration, ServiceName } from '../../../assets/config/environment';
import { PromiseFunctions } from '../util/promise-functions';

export abstract class HealthService {
    public initialized$ = new ReplaySubject<boolean>();
    protected serviceName: ServiceName;
    protected pingInterval = 10000;
    protected activeUrlIndex = 0;
    protected urls: ServiceConfiguration[] = [];

    public activeUrl$ = new BehaviorSubject<string>('');
    public healthCheckSubscription: Subscription;
    protected initializationSubscription: Subscription;
    protected active = false;

    protected constructor(
        protected http: HttpClient,
        protected name: ServiceName,
        protected endpoints: ServiceConfiguration[],
        protected healthEndpoint = '/actuator/health/readiness',
        protected enabled = true,
        protected retryInitialization = true
    ) {
        this.serviceName = name;
        this.urls = endpoints;
        this.activeUrl$.next(this.urls[this.activeUrlIndex]?.url);
        if (enabled) {
            this.initializeHealthChecks();
        } else {
            this.initialized$.next(true);
        }
    }

    protected initializeHealthChecks() {
        this.active = true;
        this.runParallelHealthCheckInitialization();
        this.scheduleHealthChecks();
    }

    protected scheduleHealthChecks() {
        this.initialized$
            .pipe(filter((initialized) => !!initialized), take(1))
            .subscribe(() => this.healthCheckSubscription = interval(this.pingInterval)
                .pipe(switchMap(() => this.pingUrls(this.urls)))
                .subscribe({
                    error: (err: HttpErrorResponse) => console.error(`${this.serviceName} Service Health Error: ${err.message}`)
                })
            );
    }

    protected runParallelHealthCheckInitialization() {
        // Race all URLs to find the first healthy one (retry if all fail until we can initialize)
        this.initializationSubscription = defer(() => PromiseFunctions.raceSuccessfulRequests(this.urls.map((url) => this.pingUrl(url.url))))
            .pipe(retry({ delay: (err) => this.retryInitialization ? timer(10000) : err}))
            .subscribe({
                next: (url) => {
                    this.activeUrlIndex = this.urls.map((service) => service.url).indexOf(url);
                    this.activeUrl$.next(this.urls[this.activeUrlIndex].url);
                    this.initialized$.next(true);
                }, error: (err) => {
                    console.error(`${this.serviceName} Service Health Initialization Error: ${err.message}`);
                    this.initialized$.next(false);
                }
            });
    }

    protected runSequentialHealthCheckInitialization() {
        // Sequentially ping all URLs to find the first healthy one
        this.initializationSubscription = defer(() => PromiseFunctions.sequentialSuccessfulRequests(this.urls.map((url) => this.pingUrl(url.url))))
            .pipe(retry({ delay: (err) => this.retryInitialization ? timer(10000) : err}))
            .subscribe({
                next: (url) => {
                    this.activeUrlIndex = this.urls.map((service) => service.url).indexOf(url);
                    this.activeUrl$.next(this.urls[this.activeUrlIndex].url);
                    this.initialized$.next(true);
                }, error: (err) => {
                    console.error(`${this.serviceName} Service Health Initialization Error: ${err.message}`);
                    this.initialized$.next(false);
                }
            });
    }

    protected pingUrls(urls: ServiceConfiguration[]): Observable<ServiceHealth> {
        return this.pingUrl(urls[this.activeUrlIndex].url).pipe(
            catchError((err) => {
                this.setActiveIndex((this.activeUrlIndex + 1) % urls.length);
                console.debug(`${this.serviceName} Health Check failed. ${err.message} Incrementing URL index`, this.activeUrlIndex);
                return of(err);
            })
        );
    }

    protected setActiveIndex(newIndex: number) {
        this.activeUrlIndex = newIndex;
        if (this.activeUrl$.value !== this.urls[newIndex].url) {
            this.activeUrl$.next(this.urls[newIndex].url);
        }
    }

    protected pingUrl(url: string) {
        return this.http.get<ServiceHealth>(url.concat(this.healthEndpoint)).pipe(
            timeout(5000),
            switchMap((res) => {
                if (res?.status === 'DOWN') {
                    return throwError(() => ({ message: 'Service down' }));
                }

                return of(url);
            }),
            catchError((err) => {
                console.debug(`${this.serviceName}: Ping failed for ${url}:`, err.message);
                return throwError(() => err);
            })
        );
    }

    public cancelScheduledHealthChecks() {
        this.active = false;
        this.healthCheckSubscription?.unsubscribe();
        this.initializationSubscription?.unsubscribe();
    }
}
